<template>
  <div
    class="friends-activation-options"
    :class="{ 'friends-activation-options-reverted': isReverted }"
  >
    <div class="fao-getapp">
      <div class="fao-getapp-top">
        <div
          v-if="isMobileView"
          class="fao-getapp-friends-logo"
        >
          <fn-image
            :src="friendsLogo.src"
            :width="friendsLogo.width"
            :height="friendsLogo.height"
            alt="AppQRCode"
          />
        </div>
      </div>
      <fn-headline type="div">{{ $t('product.friends.modal.app.headline') }}</fn-headline>
      <div class="fao-getapp-cols">
        <ul class="fao-getapp-steps">
          <li>
            <fn-icon icon="download-new" /><span>{{ $t('product.friends.modal.app.step1') }}</span>
          </li>
          <li>
            <fn-icon icon="login" /><span>{{ $t('product.friends.modal.app.step2') }}</span>
          </li>
          <li>
            <fn-icon icon="discount-outline" /><span>{{ $t('product.friends.modal.app.step3') }}</span>
          </li>
        </ul>
        <div class="fao-getapp-qr-code-logo">
          <div
            v-if="!isMobileView"
            class="fao-getapp-qr-code"
          >
            <fn-qr-code
              :text="qrCode.text"
              :size="qrCode.size"
            />
          </div>
          <div
            v-if="!isMobileView"
            class="fao-getapp-friends-logo"
          >
            <fn-image
              :src="friendsLogo.src"
              :width="friendsLogo.width"
              :height="friendsLogo.height"
              alt="AppQRCode"
            />
          </div>
        </div>
      </div>
      <fn-button
        v-if="isMobileView"
        :href="friendsActivationLink"
        target="_blank"
        full-width
        icon="download-new"
        size="m"
      >
        {{ $t('product.friends.modal.app.cta') }}
      </fn-button>
    </div>
    <div class="fao-login">
      <fn-headline type="div">{{ $t('product.friends.modal.logIn.headline') }}</fn-headline>
      <fn-vuex-form-login
        :redirect-enabled="false"
        @submit="() => $emit('submit')"
      />
    </div>
    <div class="fao-bg">
      <img
        v-for="n in 3"
        :key="n"
        src="@/assets/img/heart.svg"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import AppLink from '@/mixins/AppLink';

/* eslint-disable import/no-duplicates */
import FriendsLogo from '@/assets/img/logos/friends-logo.svg';
import FriendsLogoSize from '@/assets/img/logos/friends-logo.svg?imageSize';
/* eslint-enable import/no-duplicates */

export default {
  mixins: [AppLink],
  emits: ['submit'],
  data: () => ({
    isRegistering: false,
  }),
  computed: {
    ...mapState(useMainStore, {
      features: 'getFeature',
      isMobileView: 'isMobileView',
    }),
    friendsLogo() {
      return {
        src: FriendsLogo,
        ...FriendsLogoSize,
      };
    },
    qrCode() {
      return {
        text: this.friendsActivationLink,
        size: 230,
      };
    },
    isReverted() {
      return this.features('isRevertedFriendsActivationOptions');
    },
  },
};
</script>

<style lang="scss" scoped>
.friends-activation-options {
  display: grid;
  grid-template-rows: 1fr 1fr;
  height: 100%;

  @include bp(m) {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: unset;
  }

  .heading {
    font-size: map-get($title, 'medium');
  }

  .fao-getapp {
    padding: 40px $pad $pad $pad;
    z-index: 1;

    @include bp(m) {
      padding: math.div($pad, 2) $pad math.div($pad, 2) 0;
    }

    .fao-getapp-top {
      display: flex;
      justify-content: space-between;

      @include bp(m, max) {
        margin-bottom: $space;
      }

      .fao-getapp-friends-logo {
        width: 126px;
        height: 32px;
        padding: 4px;
        box-sizing: content-box;
      }
    }

    .fao-getapp-cols {
      display: grid;
      grid-template-columns: 1fr min-content;
      gap: $pad;

      .fao-getapp-steps {
        margin: 0;
        padding: 0;

        li {
          display: grid;
          grid-template-columns: min-content 1fr;
          gap: $pad;
          list-style: none;
          align-items: center;
          margin-bottom: $space;

          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;

            .icon {
              &::after {
                display: none;
              }
            }
          }

          .icon {
            width: 42px;
            height: 42px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: $color-general-icons-primary-default;
            border: map-get($border-width, 'xs') solid $color-general-border-neutral-default;
            border-radius: map-get($border-radiuses, 'full');
            background: $color-general-background-neutral-default;
            font-size: map-get($body, 'large');

            &::after {
              content: '';
              position: absolute;
              display: block;
              width: 1px;
              height: 100%;
              border-right: map-get($border-width, 'xs') dashed $color-general-border-neutral-default;
              bottom: -100%;
            }
          }
        }
      }

      + .button {
        margin-top: $space;
      }

      .fao-getapp-qr-code-logo {
        display: flex;
        flex-direction: column;
        align-items: end;

        @include bp(m, max) {
          margin-bottom: $space;
        }

        .fao-getapp-qr-code {
          width: 230px;
          padding: $grid-content-tile-padding;
          box-sizing: content-box;
          background: $color-general-background-neutral-default;
        }

        .fao-getapp-friends-logo {
          width: 126px;
          height: 32px;
          box-sizing: content-box;
          padding-right: $pad;
        }
      }
    }
  }

  .fao-login {
    margin: 0 (-$pad) (-$pad) (-$pad);
    background: $color-general-background-neutral-lowest;
    padding: $pad * 2;
    z-index: 1;

    @include bp(m) {
      margin: (-$pad * 2) (-$pad * 2) (-$pad * 2) 0;
      padding: 40px $pad * 2;
    }
  }

  .fao-bg {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 50%;

    @include bp(m) {
      width: 66%;
      height: 100%;
    }

    img {
      position: absolute;

      &:nth-child(1) {
        transform: rotate(-20deg);
        width: 70px;
        height: 61px;
        top: 18px;
        right: 85px;

        @include bp(m) {
          right: 445px;
        }
      }

      &:nth-child(2) {
        transform: rotate(21.7deg);
        width: 125px;
        height: 110px;
        top: 120px;
        right: -20px;

        @include bp(m) {
          width: 165px;
          height: 145px;
          right: 315px;
        }
      }

      &:nth-child(3) {
        transform: rotate(-20deg);
        width: 59px;
        height: 52px;
        right: 75px;
        top: 265px;

        @include bp(m) {
          right: 445px;
          top: 315px;
        }
      }
    }
  }

  &.friends-activation-options-reverted {
    @include bp(m) {
      grid-template-columns: 1fr 2fr;
    }

    .fao-getapp {
      order: 2;

      @include bp(m) {
        padding-left: 32px;
      }
    }

    .fao-login {
      order: 1;
      margin-top: -16px;
      padding-top: $space * 2;

      @include bp(m) {
        margin: -32px 0 -32px -32px;
      }
    }

    .fao-bg {
      top: unset;
      bottom: 0;

      @include bp(m) {
        right: 0;
        left: unset;
      }
    }
  }
}
</style>
